import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CrossIcon, RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

export type MobileRoutalSnackbarVariants = `success` | `error`;

const getIconByVariant = (variant: MobileRoutalSnackbarVariants) => {
  if (variant === `success`) {
    return CheckCircleIcon;
  }
  return CancelIcon;
};

const MobileRoutalSnackbarContainer = styled.div<{
  variant: MobileRoutalSnackbarVariants;
  showCloseButton?: boolean;
}>`
  position: fixed;
  bottom: 0;
  background-color: transparent;
  min-height: 30px;
  max-height: 150px;
  padding: 12px;
  overflow: auto;
  border-radius: 10px;
  box-shadow: 0px 0px 8px 4px rgba(125, 154, 150, 0.15);

  ${({ showCloseButton }) => (showCloseButton ? `padding-right: 26px;` : ``)}
  min-width: 200px;
  max-width: 300px;
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  ${({ variant }) => {
    if (variant === `success`) {
      return `
        background-color: ${RoutalPalette.success.ultraLight};
        & > * {
          color: ${RoutalPalette.success.medium}!important;
        }
      `;
    }
    return `
      background-color: ${RoutalPalette.error.ultraLight};
      & > * {
        color: ${RoutalPalette.error.medium}!important;
      }
    `;
  }}
`;

interface SnackbarComponentProps {
  open?: boolean;
  variant?: MobileRoutalSnackbarVariants;
  autoHideDuration?: number;
  containerStyle?: React.CSSProperties;
  showCloseButton?: boolean;
  onClose?: (...args: any[]) => any;
  onClick?: () => void;
}

export const MobileRoutalSnackbar = ({
  open,
  children,
  variant = `success`,
  autoHideDuration = 1000,
  containerStyle = {},
  showCloseButton = false,
  onClose,
  onClick,
}: React.PropsWithChildren<SnackbarComponentProps>) => {
  const [openSnack, setOpenSnack] = useState<boolean>(open ?? false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (open !== undefined && open !== openSnack) {
      setOpenSnack(open);
    }
    if (open) {
      timeout = setTimeout(() => {
        setOpenSnack(false);
        onClose?.();
      }, autoHideDuration);
    }
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const Icon = getIconByVariant(variant);

  if (!open || !openSnack) return null;

  return (
    <MobileRoutalSnackbarContainer
      variant={variant}
      style={containerStyle}
      showCloseButton={showCloseButton}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {showCloseButton && (
        <CrossIcon
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          style={{
            position: `absolute`,
            right: `12px`,
            top: `16px`,
            width: `14px`,
            cursor: `pointer`,
            color: RoutalPalette.white,
          }}
        />
      )}
      <Icon style={{ marginRight: `8px`, color: RoutalPalette.white }} />
      {children}
    </MobileRoutalSnackbarContainer>
  );
};
